import { create } from 'zustand';

import type { AnalyticsAdapter } from '@/adapters/analytics/types';
import type { Logger } from '@/adapters/logger/types';
import type { SecureStoreAdapter } from '@/adapters/storage/types';
import { ANALYTICS_EVENTS, ANALYTICS_PARAMS } from '@/constants/analytics';

export const AI_TRANSCRIPTION_CONSENT_STORAGE_KEY = 'aiTranscriptionConsentAccepted';

export interface AiTranscriptionConsentAdapters {
  secureStore: SecureStoreAdapter;
  logger: Logger;
  analytics: AnalyticsAdapter;
}

export interface AiTranscriptionConsentState {
  isAccepted: boolean;
  isLoading: boolean;
}

export interface AiTranscriptionConsentActions {
  loadConsent: () => Promise<void>;
  acceptConsent: () => Promise<void>;
}

const createAiTranscriptionConsentStore = (adapters: AiTranscriptionConsentAdapters) =>
  create<AiTranscriptionConsentState & AiTranscriptionConsentActions>((set) => ({
    isAccepted: false,
    isLoading: true,

    loadConsent: async () => {
      try {
        const stored = await adapters.secureStore.getItem(AI_TRANSCRIPTION_CONSENT_STORAGE_KEY);
        const isAccepted = stored === 'true';

        adapters.analytics.track(ANALYTICS_EVENTS.APP_AI_TRANSCRIPTION_CONSENT_LOADED, {
          [ANALYTICS_PARAMS.ACCEPTED]: isAccepted,
        });
        set({
          isAccepted,
          isLoading: false,
        });
      } catch (error) {
        adapters.logger.captureException(error);
        set({ isLoading: false });
      }
    },

    acceptConsent: async () => {
      set({ isAccepted: true });
      adapters.analytics.track(ANALYTICS_EVENTS.APP_AI_TRANSCRIPTION_CONSENT_ACCEPTED);

      try {
        await adapters.secureStore.setItem(AI_TRANSCRIPTION_CONSENT_STORAGE_KEY, 'true');
      } catch (error) {
        adapters.logger.captureException(error);
      }
    },
  }));

export { createAiTranscriptionConsentStore };
